import { useEffect, useRef, useState } from 'react';
import TaskPipelineModal, { type PipelineStep, type TaskPipelineInsight } from './taskPipelineModal/TaskPipelineModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LoadingProps {
  percentage: number;
  setProgressBarPercent: (v: number) => void;
  setIsLoading: (v: boolean) => void;
  userData: any;
  firstRender: boolean;
}

// ─── Pipeline definition ──────────────────────────────────────────────────────

const STEPS: PipelineStep[] = [
  {
    label: 'Analysing Script',
    activeText: 'Reading your script and preparing scenes…',
    doneText: 'Script ready',
  },
  {
    label: 'Generating Scenes',
    activeText: 'Building your canvas and placing elements…',
    doneText: 'Scenes created',
  },
  {
    label: 'Processing Media',
    activeText: 'Fetching images, video clips and audio…',
    doneText: 'Media loaded',
  },
  {
    label: 'Finalising',
    activeText: 'Applying finishing touches…',
    doneText: 'Ready!',
  },
];

const INSIGHTS: TaskPipelineInsight[] = [
  {
    label: 'Pro tip',
    text: 'Lip-synced avatars, watermark-free exports and transparent backgrounds are available in the final download.',
  },
  {
    label: 'Did you know',
    text: "You can swap any scene's media after generation — just click the element and pick a new image or clip.",
  },
  {
    label: 'Pro tip',
    text: 'Use the Script panel to adjust voiceover text per section without re-generating the entire video.',
  },
  {
    label: 'Did you know',
    text: 'Transitions between scenes are fully customisable — tap the Transition button between any two sections.',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Map 0-100% linearly to a step index (0 → STEPS.length). */
const percentToStep = (pct: number): number =>
  Math.min(STEPS.length, Math.floor((pct / 100) * STEPS.length));

// ─── Loading ──────────────────────────────────────────────────────────────────

export const Loading = ({
  percentage,
  setProgressBarPercent,
  setIsLoading,
}: LoadingProps) => {
  // Monotonically-increasing local copy so the ring never goes backward.
  const [localPercent, setLocalPercent] = useState(percentage);
  const stuckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalPercent((prev) => Math.max(prev, percentage));
  }, [percentage]);

  // Stuck-at-high-percent safeguard: mirrors the original 10-second timeout.
  useEffect(() => {
    if (localPercent >= 92 && localPercent < 100) {
      stuckTimer.current = setTimeout(() => {
        setProgressBarPercent(100);
      }, 10_000);
    }
    return () => {
      if (stuckTimer.current) clearTimeout(stuckTimer.current);
    };
  }, [localPercent, setProgressBarPercent]);

  const currentStep = percentToStep(localPercent);

  const handleComplete = () => {
    // Brief pause so the checkmark animates before the modal closes.
    setTimeout(() => setIsLoading(false), 600);
  };

  return (
    <TaskPipelineModal
      open
      title="Generating your *video*"
      steps={STEPS}
      currentStep={currentStep}
      progressMode="manual"
      percent={localPercent}
      ringLabel="Complete"
      insights={INSIGHTS}
      insightInterval={4000}
      onComplete={localPercent >= 100 ? handleComplete : undefined}
      zIndex={9999}
    />
  );
};
